import React, { useState, useEffect } from 'react';
import './EventDetail.css';
import EventTabs from './EventTabs';
import supabase from "../../../supabaseClient";
import { useAuth } from '../../../contexts/authContext';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaEllipsisV } from 'react-icons/fa';

const EventDetail = ({ event, onBack }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userProfile = user
    ? {
        id: user.id,
        display_name: user.display_name || user.name || user.email,
        avatar_url: user.avatar_url || null,
      }
    : null;
  const [activeTab, setActiveTab] = useState('about');
  const [attendees, setAttendees] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loadingAttendees, setLoadingAttendees] = useState(true);
  const [userAttendanceStatus, setUserAttendanceStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [showBannerControls, setShowBannerControls] = useState(false);
  const [showEventMenu, setShowEventMenu] = useState(false);

  useEffect(() => {
    if (!event?.id || !user) return;
    setLoadingAttendees(true);
    // Fetch attendees and their roles for this event
    const fetchAttendees = async () => {
      const { data, error } = await supabase
        .from('event_attendees')
        .select('user_id, role, profiles:profiles!event_attendees_user_id_fkey(id, name, display_name, avatar_url, location, occupation, native_languages, target_languages, bio, interests)')
        .eq('event_id', event.id);
      if (error) {
        setAttendees([]);
        setHosts([]);
        setGuests([]);
      } else {
        setAttendees(data.map(a => ({ ...a.profiles, role: a.role, user_id: a.user_id })));
        setHosts(data.filter(a => a.role === 'host').map(a => ({ ...a.profiles, role: a.role, user_id: a.user_id })));
        setGuests(data.filter(a => a.role === 'guest').map(a => ({ ...a.profiles, role: a.role, user_id: a.user_id })));
        
        // Check if current user is attending and their role
        const userAttendance = data.find(a => a.user_id === user.id);
        setUserAttendanceStatus(userAttendance ? userAttendance.role : null);
      }
      setLoadingAttendees(false);
    };
    fetchAttendees();
  }, [event, user]);

  const handleCancelEvent = async () => {
    if (!user || !event?.id) return;

    const isAlreadyCancelled = event.status === 'cancelled';

    if (!isAlreadyCancelled) {
      const confirmed = window.confirm(
        'Mark this event as cancelled? Attendees will be notified, and the event will remain visible as cancelled until you choose to delete it.'
      );
      if (!confirmed) return;

      try {
        const { data: attendees } = await supabase
          .from('event_attendees')
          .select('user_id')
          .eq('event_id', event.id);

        if (attendees) {
          const notifications = attendees.map(({ user_id: attendee_id }) => ({
            user_id: attendee_id,
            from_user_id: user.id,
            type: 'event_cancelled',
            title: 'Event Cancelled',
            content: 'An event you were planning to attend has been cancelled.',
            is_read: false,
            data: { event_id: event.id }
          }));

          await supabase.from('notifications').insert(notifications);
        }

        const { error: updateError } = await supabase
          .from('events')
          .update({ status: 'cancelled' })
          .eq('id', event.id);

        if (updateError) throw updateError;

        // Reflect cancelled status locally so UI updates without a full refetch
        event.status = 'cancelled';
        alert('Event marked as cancelled. Attendees have been notified.');
      } catch (error) {
        console.error('Error cancelling event:', error);
        alert('Failed to cancel event');
      }
    } else {
      const confirmed = window.confirm(
        'This event is already cancelled. Delete it permanently? This will remove the event and its attendees.'
      );
      if (!confirmed) return;

      try {
        await Promise.all([
          supabase.from('events').delete().eq('id', event.id),
          supabase.from('event_attendees').delete().eq('event_id', event.id)
        ]);

        alert('Event deleted successfully');
        navigate('/app/events');
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('Failed to delete event');
      }
    }
  };

  useEffect(() => {
    if (!event?.created_by) return;
    // Fetch creator profile
    const fetchCreatorProfile = async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, name, email')
        .eq('id', event.created_by)
        .single();
      if (!error && data) setCreatorProfile(data);
    };
    fetchCreatorProfile();
  }, [event]);

  useEffect(() => {
    if (user && event && event.created_by === user.id) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
  }, [user, event]);

  const handleBannerFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleBannerUpload = async () => {
    if (!bannerFile || !event) return;
    setBannerUploading(true);
    try {
      const fileExt = bannerFile.name.split('.').pop();
      const fileName = `event_banner_${event.id}_${Date.now()}.${fileExt}`;
      const filePath = `event-banners/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('banners')
        .upload(filePath, bannerFile, { upsert: true });
      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('banners').getPublicUrl(filePath);
      const publicUrl = data?.publicUrl;
      if (!publicUrl) throw new Error('Failed to get public URL');

      const { error: updateError } = await supabase
        .from('events')
        .update({ event_banner_url: publicUrl })
        .eq('id', event.id);
      if (updateError) throw updateError;

      // Optimistically update local event object if possible
      event.event_banner_url = publicUrl;
      setBannerFile(null);
      setBannerPreview(null);
      setShowBannerControls(false);
    } catch (err) {
      console.error('Failed to upload event banner:', err);
      alert('Failed to upload event banner.');
    } finally {
      setBannerUploading(false);
    }
  };

  const handleAttendanceChange = async (newStatus) => {
    if (!user || !event?.id || isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      if (userAttendanceStatus) {
        // User is already attending, update their status
        if (newStatus === 'leave') {
          // Remove user from attendees
          const { error } = await supabase
            .from('event_attendees')
            .delete()
            .eq('event_id', event.id)
            .eq('user_id', user.id);
          
          if (error) throw error;
          setUserAttendanceStatus(null);
        } else {
          // Update user's role
          const { error } = await supabase
            .from('event_attendees')
            .update({ role: newStatus })
            .eq('event_id', event.id)
            .eq('user_id', user.id);
          
          if (error) throw error;
          setUserAttendanceStatus(newStatus);
        }
      } else {
        // User is not attending, add them
        const { error } = await supabase
          .from('event_attendees')
          .insert({
            event_id: event.id,
            user_id: user.id,
            role: newStatus,
            joined_at: new Date().toISOString()
          });
        
        if (error) throw error;
        setUserAttendanceStatus(newStatus);
      }
      
      // Refresh attendees list
      const { data, error } = await supabase
        .from('event_attendees')
        .select('user_id, role, profiles:profiles!event_attendees_user_id_fkey(id, name, display_name, avatar_url, location, occupation, native_languages, target_languages, bio, interests)')
        .eq('event_id', event.id);
      
      if (!error && data) {
        setAttendees(data.map(a => ({ ...a.profiles, role: a.role, user_id: a.user_id })));
        setHosts(data.filter(a => a.role === 'host').map(a => ({ ...a.profiles, role: a.role, user_id: a.user_id })));
        setGuests(data.filter(a => a.role === 'guest').map(a => ({ ...a.profiles, role: a.role, user_id: a.user_id })));
      }
    } catch (error) {
      console.error('Error updating attendance:', error);
      alert('Failed to update attendance status');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail || !user || !event?.id || isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      // First try to find the user by email
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', inviteEmail)
        .single();
      
      if (userError && userError.code !== 'PGRST116') {
        throw userError;
      }
      
      if (userData) {
        // User exists, create an invite
        const { error } = await supabase
          .from('group_event_invites')
          .insert({
            inviter_id: user.id,
            invitee_id: userData.id,
            group_or_event: event.id,
            created_at: new Date().toISOString()
          });
        
        if (error) throw error;
        alert(`Invitation sent to ${inviteEmail}`);
        setInviteEmail('');
        setShowInviteModal(false);
      } else {
        alert('User not found with this email');
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      alert('Failed to send invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="event-detail">
      <div className="event-detail-header">
        <button className="event-detail-back-btn" onClick={onBack}>&larr; Back</button>
        {isAdmin && (
          <div className="event-header-kebab-wrapper">
            <button
              type="button"
              className="event-header-kebab-btn"
              onClick={() => setShowEventMenu(prev => !prev)}
            >
              <FaEllipsisV />
            </button>
            {showEventMenu && (
              <div className="event-header-kebab-menu">
                <button
                  type="button"
                  className={`event-header-kebab-item ${event.status === 'cancelled' ? '' : 'danger'}`}
                  onClick={() => {
                    setShowEventMenu(false);
                    handleCancelEvent();
                  }}
                >
                  {event.status === 'cancelled' ? 'Delete Event' : 'Cancel Event'}
                </button>
              </div>
            )}
          </div>
        )}
        {user && event.created_by === user.id && (
          <>
            <button
              type="button"
              className="event-banner-edit-btn"
              onClick={() => setShowBannerControls(!showBannerControls)}
              title="Change event banner image"
            >
              <FaEdit />
            </button>
            {showBannerControls && (
              <div className="event-banner-upload-controls">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBannerFileChange}
                  style={{ display: 'inline-block', marginRight: 8 }}
                  disabled={bannerUploading}
                />
                {bannerPreview && (
                  <img
                    src={bannerPreview}
                    alt="Preview"
                    className="event-banner-preview"
                  />
                )}
                <button
                  type="button"
                  onClick={handleBannerUpload}
                  disabled={!bannerFile || bannerUploading}
                  style={{ marginRight: 8 }}
                >
                  {bannerUploading ? 'Uploading...' : (event.event_banner_url ? 'Change Banner' : 'Upload Banner')}
                </button>
                {bannerFile && !bannerUploading && (
                  <button
                    type="button"
                    onClick={() => { setBannerFile(null); setBannerPreview(null); }}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  className="event-banner-controls-close"
                  onClick={() => setShowBannerControls(false)}
                >
                  Close
                </button>
              </div>
            )}
          </>
        )}
        <div className="event-header-content">
          {/* Top right actions */}
          <div className="event-header-actions">
            <button className="invite-btn header-action-btn" onClick={() => setShowInviteModal(true)}>Invite</button>
            <button className="share-btn header-action-btn">Share</button>
          </div>
          {event.status === 'cancelled' && (
            <span className="event-status-badge cancelled">Cancelled</span>
          )}
          <h2 className="event-title">
            {event.title}
          </h2>
          {(event.language || event.level) && (
            <p className="event-language-line">
              {event.language}
              {event.language && event.level && ' • '}
              {event.level && `Level: ${event.level}`}
            </p>
          )}
          {event.status === 'cancelled' ? (
            <div className="event-cancelled-message">
              This event has been cancelled by the host.
            </div>
          ) : (
            <div className="event-actions">
              {event.room_id && (
                <button
                  className="join-virtual-btn"
                  type="button"
                  onClick={() => navigate(`/app/virtualcafe/${event.room_id}`)}
                >
                  Join Virtual Room
                </button>
              )}
              {user && (
                <div className="attendance-actions">
                  {userAttendanceStatus ? (
                    <>
                      <span className="attendance-status">You are {userAttendanceStatus}</span>
                      <button 
                        className={`attendance-btn ${userAttendanceStatus === 'going' ? 'active' : ''}`}
                        onClick={() => handleAttendanceChange('going')}
                        disabled={isSubmitting}
                      >
                        Going
                      </button>
                      <button 
                        className={`attendance-btn ${userAttendanceStatus === 'interested' ? 'active' : ''}`}
                        onClick={() => handleAttendanceChange('interested')}
                        disabled={isSubmitting}
                      >
                        Interested
                      </button>
                      <button 
                        className="leave-btn"
                        onClick={() => handleAttendanceChange('leave')}
                        disabled={isSubmitting}
                      >
                        Leave
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        className="attendance-btn"
                        onClick={() => handleAttendanceChange('going')}
                        disabled={isSubmitting}
                      >
                        Going
                      </button>
                      <button 
                        className="attendance-btn"
                        onClick={() => handleAttendanceChange('interested')}
                        disabled={isSubmitting}
                      >
                        Interested
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {showInviteModal && (
        <div className="invite-modal">
          <div className="invite-modal-content">
            <button className="close-btn" onClick={() => setShowInviteModal(false)}>×</button>
            <h3>Invite to Event</h3>
            <form onSubmit={handleInvite}>
              <div className="form-group">
                <label>Email Address</label>
                <input 
                  type="email" 
                  value={inviteEmail} 
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div className="form-actions">
                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <EventTabs 
        event={event} 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        hosts={hosts} 
        guests={guests} 
        attendees={attendees} 
        loadingAttendees={loadingAttendees} 
        userProfile={userProfile}
        supabase={supabase}
        creatorProfile={creatorProfile}
        userAttendanceStatus={userAttendanceStatus}
        onAttendanceChange={handleAttendanceChange}
      />
    </div>
  );
};

export default EventDetail;